import { Injectable, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { Menu } from '../models/menu.model';

@Injectable({
  providedIn: 'root',
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(
    window.innerWidth
  );

  // Search Box
  public search = false;

  // Language
  public language = false;

  // Mega Menu
  public megaMenu = false;
  public levelMenu = false;
  public megaMenuColapse: boolean = window.innerWidth < 1199 ? true : false;

  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth < 991 ? true : false;

  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth < 991 ? false : true;

  // Full screen
  public fullScreen = false;
  active: any;

  constructor(
    private router: Router,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {
    this.setScreenWidth(window.innerWidth);
    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }
        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });
    if (window.innerWidth < 991) {
      // Detect Route change sidebar close
      this.router.events.subscribe((event) => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }

    // Atualiza o menu quando o estado de autenticação mudar
    this.authService.authState$.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
      this.updateMenuItems();
    });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((count) => {
        if (this.authService.canViewNotifications()) {
          this.updateNotificationBadge(count);
        }
      });
  }

  ngOnDestroy() {
    this.unsubscriber.next;
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  private getMenuItems(): Menu[] {
    const allMenuItems: Menu[] = [
      { icon: 'house', path: 'dashboard', title: 'Dashboard', type: 'link' },
      { icon: 'bell', path: 'notifications', title: 'Notificações', type: 'link', badgeClass: 'primary', badgeText: 'primary' },
      { icon: 'clipboard', path: 'users', title: 'Administradores', type: 'link' },
      {
        icon: 'people',
        title: 'Colaboradores',
        type: 'sub',
        children: [
          { path: 'collaborators/list', title: 'Lista', type: 'link' },
          { path: 'collaborators/colaboradores-permissoes', title: 'Permissões de colaboradores', type: 'link' },
        ],
      },
      {
        icon: 'clipboard',
        title: 'Processos',
        type: 'sub',
        children: [
          { path: 'processes/em-andamento', title: 'Em andamento', type: 'link' },
          { path: 'processes/concluidos-fechados', title: 'Concluídos/Fechados', type: 'link' },
          { path: 'processes/list', title: 'Todos', type: 'link' },
        ],
      },
      {
        icon: 'file-earmark-person',
        title: 'Informadores',
        type: 'sub',
        children: [
          { path: 'informers/cardeais', title: 'Cardeais', type: 'link' },
          { path: 'informers/dom', title: 'Dom', type: 'link' },
          { path: 'informers/padres', title: 'Padres', type: 'link' },
          { path: 'informers/leigos', title: 'Leigos', type: 'link' },
          { path: 'informers/list', title: 'Todos', type: 'link' },
        ],
      },
      { icon: 'person-plus', path: 'pre-informers', title: 'Pré-informadores', type: 'link' },
      { icon: 'folder', path: 'files', title: 'Gerenciar Arquivos', type: 'link' },
      { icon: 'envelope', path: '/inbox', title: 'Mensagem', type: 'link' },
      { icon: 'stars', path: '/ai-chat', title: 'Nunciatura IA', type: 'link' },
      { icon: 'gear', path: 'settings', title: 'Configurações', type: 'link' },
    ];

    // Se for colaborador, filtrar itens do menu
    if (this.authService.isColaborador()) {
      // Colaboradores veem: Dashboard, Colaboradores, Processos (Em andamento), Gerenciar Arquivos e Nunciatura IA
      return allMenuItems
        .filter(item =>
          item.path === 'dashboard' ||
          item.path === 'files' ||
          item.path === '/ai-chat' ||
          item.title === 'Colaboradores' ||
          item.title === 'Processos'
        )
        .map((item) => {
          if (item.title === 'Colaboradores') {
            return { icon: 'people', path: 'collaborators', title: 'Colaboradores', type: 'link' };
          }

          if (item.title !== 'Processos' || !item.children) {
            return item;
          }

          return {
            ...item,
            children: item.children.filter(
              (child) => child.path === 'processes/em-andamento'
            ),
          };
        });
    }

    // Admin vê tudo
    return allMenuItems;
  }

  MENUITEMS: Menu[] = this.getMenuItems();

  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

  /**
   * Atualiza o menu baseado no role do usuário
   */
  public updateMenuItems(): void {
    this.MENUITEMS = this.getMenuItems();

    if (this.authService.canViewNotifications()) {
      this.updateNotificationBadge(this.notificationService.getCurrentUnreadCount());
    }

    this.items.next(this.MENUITEMS);
  }

  /**
   * Atualiza o badge de notificações no menu lateral
   * @param count Contagem de notificações não lidas
   */
  public updateNotificationBadge(count: number): void {
    const notificationItem = this.MENUITEMS.find((item) => item.path === 'notifications');

    if (notificationItem) {
      notificationItem.badgeValue = count > 0 ? count.toString() : undefined;
      this.items.next([...this.MENUITEMS]);
    }
  }
}

